import type { AccountingTeam, TeamFigures } from "@dentacore/shared-types";
import { perHourCents } from "@dentacore/shared-types";

import { MonthSelect, YearPicker } from "./controls";
import { hoursText, money, monthName, plural } from "./format";
import { signClass, WorkLegend } from "./figures";
import styles from "./accounting.module.css";
import { PagedRows } from "../ui/paging";

/** One line of either table: who, and what they booked and finished. */
interface Line extends TeamFigures {
  key: string;
  label: string;
  /** Under the name: a job, or the location of a chair. */
  sub: string | null;
}

/**
 * Team — each provider and each chair, for a month or a year (STORY-184b,
 * TM-D1–TM-D6).
 *
 * **Every figure is the API's**; this draws it. A provider or a chair reads
 * *expected* (every booking but cancelled and no-show) behind *realized* (the
 * finished ones), each as price with profit beside it, priced exactly as Daily
 * and Monthly price a booking — so each table adds up to the clinic's figures.
 * A booking is credited to its primary provider only, and a booking with no
 * provider is its own row (TM-D2). A chair also reads its hours and what an
 * hour earned, never a percentage busy (TM-D3): DentaCore holds no honest
 * denominator for one.
 */
export function TeamTab({
  data,
  year,
  month,
  yearOptions,
  today,
  onYear,
  onMonth,
  currency,
  selectionLabel,
  showLocation,
}: {
  /** Null while it loads. */
  data: AccountingTeam | null;
  year: number;
  /** 0 is the whole year. */
  month: number;
  yearOptions: readonly number[];
  today: string;
  onYear: (year: number) => void;
  onMonth: (month: number) => void;
  currency: string;
  selectionLabel: string;
  /** Name a chair's location only when more than one is selected (TM-D5). */
  showLocation: boolean;
}): React.JSX.Element {
  const period = month === 0 ? String(year) : `${monthName(month)} ${String(year)}`;

  const controls = (
    <div className={styles.controls}>
      <YearPicker
        years={yearOptions}
        value={year}
        onChange={onYear}
        testId="accounting-team-year"
      />
      <MonthSelect
        year={year}
        today={today}
        value={month}
        onChange={onMonth}
        label="Month"
        testId="accounting-team-month"
      />
      <p className={styles.note}>Showing {selectionLabel}.</p>
    </div>
  );

  if (data === null) {
    return (
      <>
        {controls}
        <div className={styles.card}>
          <p className={styles.blank}>Loading…</p>
        </div>
      </>
    );
  }

  const providers: Line[] = data.providers.map((p) => ({
    ...p,
    key: p.userId ?? "none",
    label: p.name,
    sub: p.job === null ? null : p.job.replace(/_/g, " ").toLowerCase(),
  }));
  const chairs: Line[] = data.chairs.map((c) => ({
    ...c,
    key: c.chairId,
    label: c.name,
    sub: showLocation ? c.branchName : null,
  }));

  return (
    <>
      {controls}
      <TeamCard
        title={`Providers, ${period}`}
        testId="accounting-team-providers"
        lines={providers}
        currency={currency}
        hours={false}
        note="A booking is credited to its primary provider. Bookings with no provider are their own row, so the rows add up to the whole."
      />
      <TeamCard
        title={`Chairs, ${period}`}
        testId="accounting-team-chairs"
        lines={chairs}
        currency={currency}
        hours
        note="Hours are each booking's own length. Per hour is the price over the hours, for expected and for finished work."
      />
    </>
  );
}

/** One table with a bar per row: expected behind realized. */
function TeamCard({
  title,
  testId,
  lines,
  currency,
  hours,
  note,
}: {
  title: string;
  testId: string;
  lines: readonly Line[];
  currency: string;
  /** A chair's table also reads hours and per hour. */
  hours: boolean;
  note: string;
}): React.JSX.Element {
  const top = Math.max(
    1,
    ...lines.map((line) => Math.max(line.expected.priceCents, line.finished.priceCents)),
  );
  const width = (cents: number): string => `${String(Math.max(0, (cents / top) * 100))}%`;
  const perHour = (cents: number, minutes: number): string => {
    const value = perHourCents(cents, minutes);
    return value === null ? "—" : money(value, currency);
  };

  return (
    <section className={styles.card} data-testid={testId}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>{title}</h2>
        <span className={styles.spacer} />
        <WorkLegend expected="Expected" realized="Realized" />
      </div>
      <div className={styles.cardBody}>
        {lines.length === 0 ? (
          <p className={styles.blank} data-testid={`${testId}-empty`}>
            No bookings in this period.
          </p>
        ) : (
          <PagedRows rows={lines} per={12} noun="rows">
            {(shown) => (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th />
                      <th aria-label="Expected behind realized" />
                      <th className={styles.right}>Booked</th>
                      <th className={styles.right}>Expected</th>
                      <th className={styles.right}>Profit</th>
                      <th className={styles.right}>Finished</th>
                      <th className={styles.right}>Realized</th>
                      <th className={styles.right}>Profit</th>
                      {hours && (
                        <>
                          <th className={styles.right}>Hours booked</th>
                          <th className={styles.right}>Hours finished</th>
                          <th className={styles.right}>Expected / h</th>
                          <th className={styles.right}>Realized / h</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((line) => (
                      <tr key={line.key}>
                        <td>
                          {line.label}
                          {line.sub !== null && <span className={styles.lineSub}>{line.sub}</span>}
                        </td>
                        <td className={styles.teamBar}>
                          <span
                            className={styles.procTrack}
                            title={`${line.label}: expected ${money(line.expected.priceCents, currency)}, realized ${money(line.finished.priceCents, currency)}`}
                          >
                            <i
                              className={styles.procGhost}
                              style={{ width: width(line.expected.priceCents) }}
                            />
                            <i
                              className={styles.procFill}
                              style={{ width: width(line.finished.priceCents) }}
                            />
                          </span>
                        </td>
                        <td className={styles.right}>{plural(line.expected.count, "booking")}</td>
                        <td className={styles.right}>
                          {money(line.expected.priceCents, currency)}
                        </td>
                        <td
                          className={`${styles.right} ${signClass(line.expected.profitCents) ?? ""}`}
                        >
                          {money(line.expected.profitCents, currency)}
                        </td>
                        <td className={styles.right}>{line.finished.count}</td>
                        <td className={styles.right}>
                          {money(line.finished.priceCents, currency)}
                        </td>
                        <td
                          className={`${styles.right} ${signClass(line.finished.profitCents) ?? ""}`}
                        >
                          {money(line.finished.profitCents, currency)}
                        </td>
                        {hours && (
                          <>
                            <td className={styles.right}>{hoursText(line.bookedMinutes)}</td>
                            <td className={styles.right}>{hoursText(line.finishedMinutes)}</td>
                            <td className={styles.right}>
                              {perHour(line.expected.priceCents, line.bookedMinutes)}
                            </td>
                            <td className={styles.right}>
                              {perHour(line.finished.priceCents, line.finishedMinutes)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PagedRows>
        )}
        <p className={styles.note}>{note}</p>
      </div>
    </section>
  );
}
